import { useEffect, useState } from "react";
import { FileText, Filter, Activity } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import usePagedRows from "../components/usePagedRows";
import Pagination from "../components/Pagination";
import "../admin-affiliate-premium.css";

export default function AffiliateAuditLog() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ actor_type: "", action: "" });
  const paged = usePagedRows(rows, 12, [filters.actor_type, filters.action]);
  const load = async () => {
    try {
      const q = new URLSearchParams();
      if (filters.actor_type) q.set("actor_type", filters.actor_type);
      if (filters.action) q.set("action", filters.action);
      const r = await api.get(`/admin/aff-audit?${q.toString()}`);
      setRows(r.data?.rows || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); }, []);

  const colorFor = (action) => {
    if ((action || "").startsWith("campaign")) return "text-amber-200";
    if ((action || "").startsWith("payout") || (action || "").startsWith("account")) return "text-emerald-300";
    if ((action || "").startsWith("broadcast")) return "text-blue-300";
    if ((action || "").startsWith("contest")) return "text-amber-300";
    if ((action || "").startsWith("ticket")) return "text-pink-300";
    return "text-white";
  };

  return (
    <div className="ap-bg p-4 lg:p-6">
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-amber-300 font-bold mb-1">SECURITY</div>
          <div className="text-3xl font-black ap-gold-text">Affiliate Audit Log</div>
          <div className="text-white/55 text-sm mt-1">Every action taken by admin or affiliate is tracked here.</div>
        </div>
      </div>

      <div className="ap-glass mb-4" style={{ padding: 14 }}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <select value={filters.actor_type} onChange={(e) => setFilters({ ...filters, actor_type: e.target.value })} className="ap-input">
            <option value="">All actors</option>
            <option value="admin">Admin</option>
            <option value="affiliate">Affiliate</option>
            <option value="system">System</option>
          </select>
          <input placeholder="Action contains…" value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} className="ap-input" />
          <button onClick={load} className="ap-btn-gold rounded-xl text-[12px] font-bold inline-flex items-center justify-center gap-2">
            <Filter size={13} /> Apply
          </button>
        </div>
      </div>

      <div className="ap-glass overflow-hidden">
        <div className="ap-table overflow-x-auto">
          <table>
            <thead><tr><th>Time</th><th>Actor</th><th>Action</th><th>Target</th><th>Meta</th></tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-white/45 py-6">No audit events.</td></tr>
              ) : paged.visible.map((r) => (
                <tr key={r.id}>
                  <td className="text-[12px] text-white/65">{(r.created_at || "").slice(0, 19).replace("T", " ")}</td>
                  <td><span className="text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-md" style={{ color: "#f4e9a1", background: "rgba(212,175,55,0.10)", border: "1px solid rgba(212,175,55,0.30)" }}>{r.actor_type}</span> <span className="text-white font-semibold ml-2">{r.actor_name}</span></td>
                  <td className={`font-mono ${colorFor(r.action)}`}>{r.action}</td>
                  <td className="font-mono text-[11px] text-white/50">{r.target_type ? `${r.target_type}:${(r.target_id || "").slice(-6)}` : "—"}</td>
                  <td><pre className="text-[10.5px] text-white/45 whitespace-pre-wrap max-w-[280px]">{Object.keys(r.meta || {}).length ? JSON.stringify(r.meta) : "—"}</pre></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination total={paged.totalRows} page={paged.page} pageSize={paged.pageSize} onChange={paged.setPage} testIdPrefix="aff-audit-pg" />
      </div>
    </div>
  );
}
